import React, { useEffect } from 'react';
import {
  FDAccordionGroup,
  FDBox,
  FDContentContainer,
  FDHeadlineText,
  FDSimpleAccordion,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMobileLayout from 'Hooks/useMobileLayout';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import scrollSelectorIntoView from 'Helpers/ScrollSelectorIntoView';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const faqGroupAnswerLayoutMetaOverrideSet = buildFacetDesignOverride();

const FAQGroupAnswerLayout = () => {
  return (
    <MetaControlField attr={'answer'} />
  );
};

const faqGroupSectionLayoutMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDTitleText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly);

const FAQGroupSectionLayout = () => {
  const { resource } = useMetaLayoutContext();
  const isMobile = useMobileLayout();
  const dir = isMobile ? 'column' : 'row';
  const id = resource.id();

  const titleWithAnchor = (
    <a id={id} href={`#${id}`}>
      <FDHeadlineText value={resource.attr('title')} />
    </a>
  );

  return (
    <React.Fragment key={id}>
      <FDStack direction={dir}>
        {isMobile ? (
          <FDBox paddingX={2} paddingY={4}>
            {titleWithAnchor}
          </FDBox>
        ) : (
          <FDBox minWidth={325}>
            {titleWithAnchor}
          </FDBox>
        )}
        <FDStack direction={'column'}>
          <FDAccordionGroup>
            {resource.attr('questions').resources().list().map((question) => {
              return (
                <FDSimpleAccordion
                  key={question.id()}
                  expansionID={question.id()}
                  title={question.attr('question')}
                >
                  <MetaFormInline
                    resource={question}
                    layout={FAQGroupAnswerLayout}
                    metaOverrideSet={faqGroupAnswerLayoutMetaOverrideSet}
                    component={React.Fragment}
                    fullWidth
                    padding={'none'}
                    noIndividualSubmitButtons
                  />
                </FDSimpleAccordion>
              );
            })}
          </FDAccordionGroup>
        </FDStack>
      </FDStack>
    </React.Fragment>
  );
};

const faqGroupLayoutMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDHeadlineText,
    _customProps: { align: 'center', size: 'lg', color: 'textSecondary' },
  }, overrideReadOnly);

const FAQGroupLayout = () => {
  const { resource } = useMetaLayoutContext();
  const sectionsResources = resource.attr('sections').resources().list();
  const isMobile = useMobileLayout();
  const titlePaddingY = isMobile ? 0 : 6;
  const questionSpacing = isMobile ? 4 : 12;

  return (
    <>
      {isMobile && (
        <FDBox paddingY={4} />
      )}
      <FDBox paddingY={titlePaddingY}>
        <MetaControlField attr={'title'} />
      </FDBox>
      <FDStack direction={'column'} flexGrow={1} spacing={questionSpacing}>
        {sectionsResources.map(section => {
          return (
            <MetaFormInline
              resource={section}
              layout={FAQGroupSectionLayout}
              metaOverrideSet={faqGroupSectionLayoutMetaOverrideSet}
              component={React.Fragment}
              fullWidth
              padding={'none'}
              noIndividualSubmitButtons
            />
          );
        })}
      </FDStack>
    </>
  );
};


const FAQGroupPageTemplate = () => {
  const { pageContentResource: resource, isPageResourceLoading } = usePageContentContext();

  const selfLinkUrl = new URL(resource.link('self').url());
  const utmSource = selfLinkUrl.searchParams.get('utm_source');
  const args = { isPageView: true, nodeName: 'FAQ', faqType: resource.id() };
  if (utmSource) {
    args.utmSource = utmSource;
  }

  useEventTrackingLogEvent(args);
  useBoneBackgroundColor();

  useEffect(() => {
    if (isPageResourceLoading) {
      return;
    }

    scrollSelectorIntoView(window.location.hash.substring(1)); // Scroll to anchor on page load
  }, [isPageResourceLoading]);

  if (!resource) {
    return null;
  }

  return (
    <FDContentContainer maxWidth={'lg'}>
      <MetaFormInline
        resource={resource}
        layout={FAQGroupLayout}
        metaOverrideSet={faqGroupLayoutMetaOverrideSet}
        component={React.Fragment}
        fullWidth
        padding={'none'}
        noIndividualSubmitButtons
      />
    </FDContentContainer>
  );
};

FAQGroupPageTemplate.defaultProps = {
};

FAQGroupPageTemplate.propTypes = {
};

export default FAQGroupPageTemplate;
